import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Link,
  Alert,
  CircularProgress,
  MenuItem,
  Grid
} from '@mui/material';
//import Grid from '@mui/material/Grid';
import { PersonAddOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { registerUser, clearError } from '../store/slices/authSlice';
import { RootState } from '../store';
import { useAppDispatch }from '../hooks/useAppDispatch'

const schema = yup.object({
  name: yup.string().required('Nombre es requerido'),
  email: yup.string().email('Email inválido').required('Email es requerido'),
  phone: yup.string().required('Teléfono es requerido'),
  country_code: yup.string().required('Código de país es requerido'),
  password: yup.string()
    .min(8, 'Mínimo 8 caracteres')
    .matches(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .matches(/[a-z]/, 'Debe contener al menos una minúscula')
    .matches(/\d/, 'Debe contener al menos un número')
    .required('Contraseña es requerida'),
  password_confirmation: yup.string()
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Confirmación es requerida'),
  role: yup.string().oneOf(['parent', 'viewer']).default('parent')
});

const countryCodes = [
  { value: '+53', label: 'Cuba (+53)' },
  { value: '+7', label: 'Rusia (+7)' },
  { value: '+1', label: 'USA/Canada (+1)' },
  { value: '+34', label: 'España (+34)' },
];

const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      role: 'parent',
      country_code: '+53'
    }
  });

  const password = watch('password');

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = async (data: any) => {
    try {
        // Despacha el thunk correctamente
        const resultAction = await dispatch(registerUser(data)).unwrap();
        // Si quieres manejar éxito (ej: redirigir)
        navigate('/dashboard');
    } catch (err) {
        // El error ya está en el estado gracias a rejectWithValue
        // Puedes mostrarlo desde el slice si lo prefieres
    }
  };

  const passwordStrength = () => {
    if (!password) return { strength: 0, label: '' };
    
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 25;
    
    let label = '';
    if (strength <= 25) label = 'Débil';
    else if (strength <= 50) label = 'Regular';
    else if (strength <= 75) label = 'Buena';
    else label = 'Excelente';
    
    return { strength, label };
  };

  const strength = passwordStrength();

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <PersonAddOutlined />
            </Avatar>
            <Box>
              <Typography component="h1" variant="h5">
                Registro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sistema Financiero Escuela Origen
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof error === 'string' ? error : 'Error en el registro'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Rol"
                  {...register('role')}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  disabled={isLoading}
                >
                  <MenuItem value="parent">Padre/Tutor</MenuItem>
                  <MenuItem value="viewer">Solo Visualización</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Código País"
                  {...register('country_code')}
                  error={!!errors.country_code}
                  helperText={errors.country_code?.message}
                  disabled={isLoading}
                >
                  {countryCodes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  {...register('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  disabled={isLoading}
                  placeholder="Ej: 5551234567"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading}
                />
                
                {password && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Fortaleza: {strength.label}
                    </Typography>
                    <Box
                      sx={{
                        height: 4,
                        width: '100%',
                        backgroundColor: 'grey.300',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mt: 0.5
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${strength.strength}%`,
                          backgroundColor:
                            strength.strength <= 25 ? 'error.main' :
                            strength.strength <= 50 ? 'warning.main' :
                            strength.strength <= 75 ? 'info.main' : 'success.main',
                          transition: 'width 0.3s'
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Confirmar Contraseña"
                  type="password"
                  {...register('password_confirmation')}
                  error={!!errors.password_confirmation}
                  helperText={errors.password_confirmation?.message}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Registrarse'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                ¿Ya tienes cuenta? Inicia sesión
              </Link>
            </Box>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Al registrarte, aceptas nuestros términos y condiciones.
          <br />
          © {new Date().getFullYear()} Sistema Financiero Escuela Origen
        </Typography>
      </Box>
    </Container>
  );
};

export default Register;